import axios from 'axios'
import {
  AccountInterface,
  AccountShipmentDto,
  AccountSiteBUDto,
  AccountSiteInterfases,
  BusinessUnitInterface,
  DeliveryEnum,
  DestinationDto,
  DestinationInterface,
  FavAndHistoricalDestinationListInterface,
  Nullable,
  PaymentMethodAccountDto,
  RecurrentDestinationInterface,
  TaxIdentificationTypeInterface,
} from '../interfaces'
import { randomAccount } from '../utils/random'
import { ListResponse } from '../interfaces/Shipment/ListResponseDto'
import { NavigationItem } from '../components'
import {
  accountMapper,
  businessUnitMapper,
  taxIdTypeIdFromAbbreviation,
} from '../utils/mappers'
import { apiDjango } from './ApiDjango'
import { splitPhoneCountryCode } from '../utils/shipmentImportUtils'
import { FavoriteAndRecentDestinationDjangoDto } from '../interfaces/Django/CatalogDjangoDto'
const { REACT_APP_URL_PATH_API, REACT_APP_URL_PATH_API_DJANGO } = process.env

export const getTaxIdentificationTypes = async (): Promise<
  TaxIdentificationTypeInterface[]
> => {
  //console.log(`[API CALL] GET Tax Identification Types`);
  return [
    {
      taxIdentificationTypeId: 1,
      taxIdentificationTypeCode: '001',
      taxIdentificationTypeName: 'Cedula Identidad',
      taxIdentificationTypeRegExp: '[V|E]{1-8}',
      countryId: 236,
      isJuridic: true,
      abbreviationName: 'V-',
    },
    {
      taxIdentificationTypeId: 2,
      taxIdentificationTypeCode: '002',
      taxIdentificationTypeName: 'Pasaporte',
      taxIdentificationTypeRegExp: '{1-9}',
      countryId: 236,
      isJuridic: true,
      abbreviationName: 'P-',
    },
    {
      taxIdentificationTypeId: 3,
      taxIdentificationTypeCode: '003',
      taxIdentificationTypeName: 'Gubernamental',
      taxIdentificationTypeRegExp: '[G]{1-8}',
      countryId: 236,
      isJuridic: false,
      abbreviationName: 'G-',
    },
    {
      taxIdentificationTypeId: 4,
      taxIdentificationTypeCode: '004',
      taxIdentificationTypeName: 'RIF',
      taxIdentificationTypeRegExp: '[J|G|V|E]-{1-8}-{0}',
      countryId: 236,
      isJuridic: false,
      abbreviationName: 'J-',
    },
    {
      taxIdentificationTypeId: 5,
      taxIdentificationTypeCode: '005',
      taxIdentificationTypeName: 'Extranjero',
      taxIdentificationTypeRegExp: '[E]{1-8}',
      countryId: 236,
      isJuridic: true,
      abbreviationName: 'E-',
    },
  ]
}

export const getAccounts = async (): Promise<AccountInterface[]> => {
  //console.log(`[API CALL] GET Accounts`);

  return new Array(Math.floor(Math.random() * 100) + 20)
    .fill(0)
    .map(() => randomAccount())
}

export const getAccount = async (
  accountId: string
): Promise<Nullable<AccountInterface>> => {
  //const uri = `${baseURLJsonServer}/accounts?id=${accountId}`;
  if (!accountId) {
    return null
  }

  const uri = `${REACT_APP_URL_PATH_API}/Account/GetAccount?accountId=${accountId}`

  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      return accountMapper(response.data)
    }
  } catch (error) {}

  return null
}

export const getFilteredAccounts = async (
  criteria: string
): Promise<Nullable<AccountInterface[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/Account?idStr=${criteria}&maxRows=20&withAgreementAuths=true`
  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return null
}

export const getFavoriteAndHistoricalLocations = async (
  accountId: string,
  taxIdentificationTypes: TaxIdentificationTypeInterface[],
  buList: BusinessUnitInterface[],
  search?: string
): Promise<ListResponse<FavAndHistoricalDestinationListInterface>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetFavoriteAndRecentDestinations?AccountID=${accountId}&SearchInput=${search}`

  try {
    const response = await axios.get(uri)
    if (
      response.status === 200 &&
      !response.data.didError &&
      !!response.data.model
    ) {
      const favoriteDestination = response.data
        .model as RecurrentDestinationInterface[]

      const favorite = favoriteDestination
        .filter((fav) => fav.tipoAdr === 'Favorito')
        .map((destination: RecurrentDestinationInterface) =>
          mapToDestiInterface(destination, taxIdentificationTypes, buList)
        )
      const Sales = favoriteDestination
        .filter((fav) => fav.tipoAdr === 'Sales')
        .map((destination: RecurrentDestinationInterface) =>
          mapToDestiInterface(destination, taxIdentificationTypes, buList)
        )

      return {
        message: '',
        didError: false,
        errorMessage: '',
        model: {
          accountID: '',
          favoriteDestinations: favorite,
          historicalDestinations: Sales,
        },
      }
    } else if (response.status === 200) {
      return response.data
    }

    return {
      model: null,
      didError: true,
      message: `Server status: ${response.status}`,
      errorMessage: 'Server error',
    }
  } catch (error) {
    return {
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: 'Application error',
    }
  }
}

/**
 * Códigos de error del envelope para GET /api/account/<id>/destinations.
 * `"0"` es OK: si el usuario no tiene cuenta vinculada y se pidió `me`, viene
 * data: [] — lista vacía, NO error.
 */
const DESTINATIONS_ERROR_MESSAGES: Record<string, string> = {
  'security.Permission.1': 'No tenés permisos para ver los destinos de esta cuenta',
  // Reemplazó a general.General.5 para account_id no-UUID y `type` fuera de
  // all/favorites/recent. Se deja el viejo mapeado por si algún ambiente va atrasado.
  'api_common.RequestError.3': 'La cuenta indicada no es válida',
  'general.General.5': 'La cuenta indicada no es válida',
  'account.Account.10':
    'Tu usuario está vinculado a más de una cuenta: indicá cuál consultar',
}

/**
 * Quita el prefijo de país del teléfono ("+584141234567" → "4141234567").
 *
 * `normalizePhone` (carga masiva) borra los no-dígitos y, al ver que "584..." no es un
 * prefijo de operadora, lo trata como FIJO y le antepone un 0: "0584141234567", 13
 * dígitos, que la validación rechaza. El destinatario quedaría inválido solo por
 * autocompletarlo desde un favorito.
 */
const stripCountryPrefix = (phone: string | null | undefined): string => {
  const raw = (phone || '').trim()
  // Solo +58, a propósito. Un `/^\+\d{1,3}/` genérico es goloso: sobre "+584141234567"
  // se comería "+584" y dejaría 9 dígitos — un número plausible pero equivocado. La app
  // es solo Venezuela (countryPhoneAccessCode va fijo en '+58'), así que cualquier otro
  // prefijo se deja intacto y falla visible en la validación en vez de mutar callado.
  return raw.startsWith('+58') ? raw.slice(3).trim() : raw
}

/**
 * Teléfono de un destino recurrente → `{ phoneNumber, countryPhoneAccessCode }`
 * del `listAccountPhone` de la cuenta.
 *
 * `RecurrentDestinationInterface` guarda el teléfono en UN solo campo, pero el
 * backend lo escribe con su código adelante ("+57 3001234567"), así que el país
 * está ahí y se puede separar. Sin código reconocible (el caso venezolano, donde
 * `stripCountryPrefix` ya se llevó el "+58 ") vale la convención de siempre.
 */
const consigneeAccountPhoneParts = (
  consigneePhone: string | null | undefined
): { phoneNumber: string; countryPhoneAccessCode: string } => {
  const { countryCode, phone } = splitPhoneCountryCode(consigneePhone || '')
  return { phoneNumber: phone, countryPhoneAccessCode: countryCode }
}

/**
 * GET /api/account/<account_id>/destinations (Django) — destinos favoritos + recientes
 * de la cuenta.
 * Drop-in de getFavoriteAndHistoricalLocations: misma firma y mismo retorno
 * ListResponse<FavAndHistoricalDestinationListInterface>, para que los consumidores solo
 * cambien el import. Adapta el DTO snake_case a RecurrentDestinationInterface y reusa
 * mapToDestiInterface (mismo mapeo a favoritos/recientes por source_type).
 *
 * `accountId` va en la RUTA (antes era query param) y acepta el alias `me` para la cuenta
 * vinculada al usuario autenticado. El texto de búsqueda viaja como `search` (antes
 * `search_value`). La ventana de ventas recientes ya no se manda: el servidor la fija en
 * 30 días. `type` (nuevo, opcional) no se envía → el servidor aplica su default `all`,
 * que es justo lo que necesitamos (favoritos + recientes, separados acá por source_type).
 *
 * Envelope Django: se ramifica por `error`/`code`, nunca por el status HTTP (siempre 200).
 * PII: no loguear la respuesta.
 */
export const getFavoriteAndRecentDestinationsDjango = async (
  accountId: string,
  taxIdentificationTypes: TaxIdentificationTypeInterface[],
  buList: BusinessUnitInterface[],
  search?: string
): Promise<ListResponse<FavAndHistoricalDestinationListInterface>> => {
  const toRecurrent = (
    d: FavoriteAndRecentDestinationDjangoDto
  ): RecurrentDestinationInterface => ({
    tipoAdr: d.source_type === 'Sales' ? 'Sales' : 'Favorito',
    // deliveryTypeID se compara con .toString() contra DeliveryEnum ('10'/'20').
    deliveryTypeID: String(
      d.delivery_type?.delivery_type_id ?? ''
    ) as unknown as DeliveryEnum,
    buCode: d.destination_store?.code ?? '',
    buAreaName: d.destination_store?.commercial_name ?? '',
    latitude: d.address?.latitude ?? 0,
    longitude: d.address?.longitude ?? 0,
    addressID: d.address?.address_id ?? '',
    addressName: d.address?.address_name ?? '',
    addressLine1: d.address?.address_line1 ?? '',
    addressLandMark: d.address?.address_land_mark ?? '',
    postalCode: d.address?.postal_code ?? '',
    consigneeAccountID: d.consignee?.id ?? '',
    consigneeFullName: d.consignee?.full_name ?? '',
    consigneePhone: stripCountryPrefix(d.consignee?.phone),
    // La API manda la abreviatura ("V-"); adentro se sigue usando el ID numérico,
    // porque esta interfaz la comparte la ruta .NET legacy.
    consigneeIdentificationType: taxIdTypeIdFromAbbreviation(
      d.consignee?.tax_identification_type,
      taxIdentificationTypes
    ),
    consigneeIdentificationNumber: d.consignee?.identification_number ?? '',
    creationDate: d.creation_date ?? '',
    consigneeEmail: d.consignee?.email ?? '',
    displayName: d.display_name ?? '',
  })

  const errMsg = 'No se pudieron obtener los destinos favoritos'

  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}/account/${encodeURIComponent(
        accountId
      )}/destinations`,
      {
        params: {
          ...(search ? { search } : {}),
        },
      }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const rows = ((resp.data.data ?? []) as FavoriteAndRecentDestinationDjangoDto[]).map(
        toRecurrent
      )
      const favorite = rows
        .filter((r) => r.tipoAdr === 'Favorito')
        .map((r) => mapToDestiInterface(r, taxIdentificationTypes, buList))
      const sales = rows
        .filter((r) => r.tipoAdr === 'Sales')
        .map((r) => mapToDestiInterface(r, taxIdentificationTypes, buList))
      return {
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
        model: {
          accountID: accountId,
          favoriteDestinations: favorite,
          historicalDestinations: sales,
        },
      }
    }
    const code = resp.data?.code as string | undefined
    const m =
      (code && DESTINATIONS_ERROR_MESSAGES[code]) || resp.data?.message || errMsg
    // Solo el code: la respuesta trae PII de los destinatarios.
    console.error('getFavoriteAndRecentDestinationsDjango: respuesta con error', {
      code,
    })
    return { model: null, didError: true, message: m, errorMessage: m }
  } catch (error) {
    const data = (error as any)?.response?.data
    const code = data?.code as string | undefined
    const m = (code && DESTINATIONS_ERROR_MESSAGES[code]) || data?.message || errMsg
    return { model: null, didError: true, message: m, errorMessage: m }
  }
}

// Method to map DestinationDto to DestinationInterface
export const mapToDestiInterface = (
  destination: RecurrentDestinationInterface,
  taxIdTypes: TaxIdentificationTypeInterface[],
  buList: BusinessUnitInterface[]
): DestinationInterface => {
  const taxIdType = taxIdTypes.find(
    (taxIdType) =>
      taxIdType.taxIdentificationTypeId ===
      destination.consigneeIdentificationType
  )

  let businessUnit: BusinessUnitInterface

  if (destination.deliveryTypeID.toString() === DeliveryEnum.OFFICE) {
    businessUnit = buList.find((bu) => bu.buCode === destination.buCode)!
  } else {
    businessUnit = {} as BusinessUnitInterface
  }

  return {
    account: {
      id: destination.consigneeAccountID,
      //accountCode: destination.accountCode,
      taxIdentificationTypeID: destination.consigneeIdentificationType,
      identificationNumber: destination.consigneeIdentificationNumber,
      accountFullName: destination.consigneeFullName,
      taxIdentificationTypeCode: taxIdType?.taxIdentificationTypeCode ?? '',
      abbreviationName: taxIdType?.abbreviationName ?? '',
      displayName: destination.displayName ?? '',
      typeSearch: destination.tipoAdr,
      listAccountPhone: destination.consigneePhone
        ? [
            {
              phoneID: '', // No disponible en RecurrentDestinationInterface
              accountId: destination.consigneeAccountID || '',
              countryId: '', // No disponible en RecurrentDestinationInterface
              // El código de país SÍ está disponible: viene dentro del teléfono.
              // El backend lo guarda como `${código} ${número}` (lo exige su
              // propio validador) y `stripCountryPrefix` solo le quita el +58,
              // así que un favorito extranjero llega acá con su "+57 " intacto.
              //
              // Antes esto se cableaba en '' y el número entero —prefijo
              // incluido— iba a `phoneNumber`. Quien lo leía asumía Venezuela y
              // `normalizePhone` lo dejaba en "0573001234567": la carga masiva
              // marcaba el favorito colombiano como inválido y, al estar los
              // campos bloqueados por el favorito, no había forma de arreglarlo
              // en la fila.
              ...consigneeAccountPhoneParts(destination.consigneePhone),
              phoneTypeId: '', // No disponible en RecurrentDestinationInterface
            },
          ]
        : [],
      listAccountEmail: destination.consigneeEmail
        ? [
            {
              emailID: '',
              accountId: destination.consigneeAccountID || '',
              email: destination.consigneeEmail,
            },
          ]
        : [],
      listAuthorizingAccount: [],
      creationDate: '',
      creationUser: '',
    },
    businessUnit: businessUnit ?? {},
    location: {
      name: destination.addressName,
      address: destination.addressLine1,
      reference: destination.addressLandMark,
      postalCode: destination.postalCode,
      coordinates: {
        lat: destination.latitude ?? 0,
        lng: destination.longitude ?? 0,
      },
    },
    store: {
      // El `?.` de `find()` no alcanzaba: `.toString()` iba sin proteger, así
      // que un favorito cuya tienda ya no está en el catálogo (retirada) tumbaba
      // con TypeError la carga ENTERA de favoritos, no solo esa fila.
      id:
        buList.find((x) => x.buCode === destination.buCode)?.buid?.toString() ??
        '',
      name: destination.buAreaName,
      code: destination.buCode,
      location: {
        name: destination.addressName ?? '',
        address: destination.addressLine1 ?? '',
        reference: destination.addressLandMark ?? '',
        postalCode: destination.postalCode ?? '',
        coordinates: {
          lat: destination.latitude ?? 0,
          lng: destination.longitude ?? 0,
        },
      },
      polygonList: [],
    },
    isHomeDelivery:
      destination.deliveryTypeID.toString() === DeliveryEnum.DELIVERY,
  }
}

// Method to map DestinationDto to DestinationInterface
export const mapToDestinationInterface = (
  destination: DestinationDto,
  taxIdTypes: TaxIdentificationTypeInterface[]
): DestinationInterface => {
  const taxIdType = taxIdTypes.find(
    (taxIdType) =>
      taxIdType.taxIdentificationTypeId === destination.taxIdentificationTypeID
  )
  return {
    account: {
      id: destination.accountID,
      accountCode: destination.accountCode,
      taxIdentificationTypeID: destination.taxIdentificationTypeID,
      identificationNumber: destination.identificationNumber,
      accountFullName: destination.accountFullName,
      displayName: destination.displayName ?? '',
      taxIdentificationTypeCode: taxIdType?.taxIdentificationTypeCode ?? '',
      abbreviationName: taxIdType?.abbreviationName ?? '',
      listAccountPhone: destination.accountPhoneList.map((phone) => ({
        phoneID: phone.accountPhoneID,
        accountID: destination.accountID,
        countryID: phone.accountPhoneCountryID,
        phoneNumber: phone.accountPhoneNumber,
        countryPhoneAccessCode: phone.accountCountryPhoneAccessCode,
        phoneTypeID: phone.accountPhoneTypeID,
      })),
      listAccountEmail:
        destination.accountEmailList?.map((email) => ({
          email: email.accountEmail,
          accountID: destination.accountID,
          emailID: email.accountEmailID,
          emailTypeId: email.accountEmailTypeID,
        })) ?? [],
      listAuthorizingAccount: [],
      creationDate: '',
      creationUser: '',
    },
    businessUnit: businessUnitMapper(destination.businessUnit),
    location: {
      name: destination.addressName,
      address:
        destination.addressLine1 +
        (destination.addressLine2 !== null
          ? ' ' + destination.addressLine2
          : ''),
      reference: destination.addressReference,
      postalCode: destination.postalCode,
      coordinates: {
        lat: destination.latitude ?? 0,
        lng: destination.longitude ?? 0,
      },
    },
    store: {
      id: destination.businessUnit?.buid.toString(),
      name: destination.businessUnit?.buName,
      code: destination.businessUnit?.buCode,
      location: {
        name: destination.businessUnit?.buAreaName ?? '',
        address: destination.businessUnit?.address ?? '',
        reference: destination.businessUnit?.reference ?? '',
        postalCode: destination.businessUnit?.postalCode ?? '',
        coordinates: {
          lat: !!destination.businessUnit?.latitude
            ? parseFloat(destination.businessUnit?.latitude)
            : 0,
          lng: !!destination.businessUnit?.longitude
            ? parseFloat(destination.businessUnit?.longitude)
            : 0,
        },
      },
      polygonList: [],
    },
    isHomeDelivery: destination.isHomeDelivery,
  }
}

export const GetModuleByUser = async (
  id?: string,
  roleID?: number
): Promise<NavigationItem[]> => {
  const uri = `${REACT_APP_URL_PATH_API}/Users/GetModuleByUser?UserID=${id}&RoleID=${roleID}&applicationCode=${'020'}`
  try {
    const response = await axios.get(uri, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (response.status === 200) {
      const responseModule = response.data
      let modules: NavigationItem[] = responseModule
      return modules
    }
  } catch (error) {
    console.log('ha ocurrido un error', error)
  }

  return []
}

export const getAccounSiteByUser = async (
  id?: string
): Promise<AccountInterface[]> => {
  const uri = `${REACT_APP_URL_PATH_API}/Users/GetAccounSiteByUser?UserID=${id}`
  try {
    const response = await axios.get(uri, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (response.status === 200) {
      const responseModule = response.data
      if (responseModule.model.length === 0) {
        return []
      }
      let AccounstSite: AccountInterface[] = []
      responseModule.model.forEach((item: any) =>
        AccounstSite.push({
          id: item.accountID as string,
          accountCode: item.accountSiteCode,
          BuCodeSource: item.buCodeSource,
          cityName: item.cityName,
          cityCode: item.cityCode,
          serviceID: item.serviceID ?? 0,
          abbreviationName: '',
          identificationNumber: '',
          accountFullName: item.accountSiteName,
          accountSiteName: item.accountSiteName,
          listAccountPhone: [],
          listAccountEmail: [],
          listAuthorizingAccount: [],
          accountSiteID: item.accountSiteID,
          accountBillToId: item.accountBillTo,
          displayName: item.displayName,
          accountShortName: item.accountShortName,
        })
      )
      return AccounstSite
      //return modules;
    }
  } catch (error) {
    console.log('ha ocurrido un error', error)
  }

  return []
}

export const GetAccountSiteByUser = async (
  accountID?: string,
  accountSiteId?: number
): Promise<ListResponse<AccountSiteInterfases[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/account/GetAccountSite?AccountID=${accountID}&AccountSiteId=${accountSiteId}`
  var response = await axios
    .get(uri)
    .then(function (response) {
      var responsedto = response.data
      return responsedto as ListResponse<AccountSiteInterfases[]>
    })
    .catch(function (error) {
      if (error.response) {
        console.log(error.message ?? '')
        return error.response as ListResponse<AccountSiteInterfases[]>
      } else {
        console.log(error.message ?? '')
      }
      return [] as unknown as ListResponse<AccountSiteInterfases[]>
    })

  return response
}

/**
 * Unidades de negocio asociadas al contrato (AccountSite).
 *
 * Falla CERRADO a propósito: cuando algo sale mal devolvemos `model: []` PERO
 * con `didError: true`. Bajo la regla restrictiva un `[]` silencioso se leería
 * como "contrato sin restricciones", y eso le devolvería al cliente las ~200
 * tiendas que el backend va a rechazar. Quien consuma esto debe distinguir
 * "sin filas" de "no pudimos consultar".
 *
 * Ojo: el endpoint responde 400 cuando AccountSiteID es 0
 * (AccountController.cs:1398-1403), así que el llamador NO debe consultarlo
 * para clientes sin contrato.
 */
export const getAccountSiteBUByAccountSiteID = async (
  accountSiteID: number
): Promise<ListResponse<AccountSiteBUDto[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/Account/GetAccountSiteBUByAccountSiteID?AccountSiteID=${accountSiteID}`

  const response = await axios
    .get(uri)
    .then(function (response) {
      const responsedto = response.data as ListResponse<AccountSiteBUDto[]>
      return {
        message: responsedto?.message ?? '',
        didError: Boolean(responsedto?.didError),
        errorMessage: responsedto?.errorMessage ?? '',
        model: responsedto?.model ?? [],
      } as ListResponse<AccountSiteBUDto[]>
    })
    .catch(function (error) {
      console.log(
        `getAccountSiteBUByAccountSiteID(${accountSiteID}): ${
          error?.message ?? error
        }`
      )
      return {
        message: '',
        didError: true,
        errorMessage: error?.message ?? 'Error consultando las tiendas del contrato',
        model: [],
      } as ListResponse<AccountSiteBUDto[]>
    })

  return response
}

export const UpdateAccountShipment = async (
  account: AccountShipmentDto
): Promise<ListResponse<AccountShipmentDto>> => {
  var response = await axios
    .post(`${REACT_APP_URL_PATH_API}/Account/UpdateShipment`, account)
    .then(function (response) {
      var data = response.data as ListResponse<AccountShipmentDto>
      return data
    })
    .catch(function (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        let errorData = error.response.data as ListResponse<AccountShipmentDto>
        console.log('Error actualizando cliente:', errorData.errorMessage)
        return errorData
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log('Error actualizando cliente:', error.message)
      }
      return [] as unknown as ListResponse<AccountShipmentDto>
    })
  return response
}

export const GetPaymentMethodAccount = async (
  accountID?: string
): Promise<ListResponse<PaymentMethodAccountDto[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/account/GetAllPaymentMethodAccount?AccountID=${accountID}`
  var response = await axios
    .get(uri)
    .then(function (response) {
      var responsedto = response.data
      return responsedto as ListResponse<PaymentMethodAccountDto[]>
    })
    .catch(function (error) {
      if (error.response) {
        console.log(error.message ?? '')
        return error.response as ListResponse<PaymentMethodAccountDto[]>
      } else {
        console.log(error.message ?? '')
      }
      return [] as unknown as ListResponse<PaymentMethodAccountDto[]>
    })

  return response
}

export const saveAccountService = async (
  account: AccountInterface
): Promise<ListResponse<AccountInterface>> => {
  account.creationDate = new Date().toISOString()
  const uri = `${REACT_APP_URL_PATH_API}/Account`

  try {
    var response = await axios.post(uri, account)
    if (response.status === 200) {
      return response.data
    }
    return {
      model: null,
      didError: true,
      message: `Server status: ${response.status}`,
      errorMessage: 'Server error',
    }
  } catch (error: any) {
    console.log(error)
    return {
      statusId: error?.response.status,
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: 'Application',
    }
  }
}
